import { formatDateTime } from '@/lib/date-time';
/**
 * @file routes/_app/bulletin.admin.tsx
 * @description 公告管理頁（admin / manager 後台）。
 *
 * 對接後端 7 個端點：
 *   GET    /api/bulletin/admin           完整列表（含未發布/封存）
 *   POST   /api/bulletin                 新增（可選擇立即發布或存草稿）
 *   PATCH  /api/bulletin/:id             編輯（標題/內文/受眾/置頂）
 *   DELETE /api/bulletin/:id             軟刪除
 *   POST   /api/bulletin/:id/archive     封存
 *   POST   /api/bulletin/:id/pin         置頂
 *   POST   /api/bulletin/:id/unpin       取消置頂
 *
 * 公告 status 由 publishedAt / archivedAt 推導：
 *   - archivedAt 非空 → 已封存
 *   - publishedAt 為 null → 草稿
 *   - 其餘 → 已發布
 */

import { createFileRoute } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Archive,
  Edit3,
  Megaphone,
  Pin,
  PinOff,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  ListPageShell,
  PageMetaItem,
  FilterBar,
  StatusPill,
  type StatusPillProps,
} from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義（對應 URL: /bulletin/admin） */
export const Route = createFileRoute('/_app/bulletin/admin')({ component: BulletinAdminPage });

/* ════════════════════════════════════════════════
 * 類型 / 常量
 * ════════════════════════════════════════════════ */

/** 公告實體（對應後端 bulletins 表） */
interface Bulletin {
  id: number;
  title: string;
  body: string;
  audience: string;
  isPinned: boolean;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
}

/** 後端回應結構：GET /api/bulletin/admin */
interface BulletinListResponse {
  items: Bulletin[];
}

/** 推導出的狀態 */
type BulletinStatus = 'draft' | 'published' | 'archived';

/** 受眾選項（與既有的 audience 字串相容；新增時用，編輯既有項可保留任意值） */
const AUDIENCE_OPTIONS = [
  { value: 'all', label: '全體用戶' },
  { value: 'admin', label: '管理員' },
  { value: 'manager', label: '管理組' },
  { value: 'auditor', label: '稽核員' },
  { value: 'decider', label: '驗證決定組' },
];

/** 狀態 → StatusPill 配色 */
const STATUS_CONFIG: Record<BulletinStatus, { label: string; tone: StatusPillProps['tone'] }> = {
  draft: { label: '草稿', tone: 'muted' },
  published: { label: '已發布', tone: 'success' },
  archived: { label: '已封存', tone: 'warning' },
};

/** 由 publishedAt / archivedAt 推導狀態 */
function deriveStatus(b: Bulletin): BulletinStatus {
  if (b.archivedAt) return 'archived';
  if (!b.publishedAt) return 'draft';
  return 'published';
}

/* ════════════════════════════════════════════════
 * 主頁面
 * ════════════════════════════════════════════════ */

function BulletinAdminPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Bulletin | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<BulletinStatus | undefined>();
  const [form] = Form.useForm<Record<string, unknown>>();

  /** 拉取完整公告列表（含未發布/封存） */
  const { data, isLoading } = useQuery({
    queryKey: ['bulletin-admin'],
    queryFn: () => apiClient.get<BulletinListResponse>('/api/bulletin/admin'),
  });

  /** 客端搜尋 + 狀態過濾（公告數量小，全量拉回後過濾足矣） */
  const allItems = useMemo(() => data?.items ?? [], [data]);
  const items = useMemo(() => {
    return allItems.filter((b) => {
      if (filterStatus && deriveStatus(b) !== filterStatus) return false;
      if (searchText && !b.title.toLowerCase().includes(searchText.toLowerCase())) return false;
      return true;
    });
  }, [allItems, searchText, filterStatus]);

  /** 統計區（基於全量計算，不受過濾影響） */
  const stats = useMemo(() => {
    let published = 0;
    let draft = 0;
    let archived = 0;
    for (const b of allItems) {
      const s = deriveStatus(b);
      if (s === 'published') published += 1;
      else if (s === 'draft') draft += 1;
      else archived += 1;
    }
    return { total: allItems.length, published, draft, archived };
  }, [allItems]);

  /* ──────── Mutations ──────── */

  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post('/api/bulletin', v),
    onSuccess: () => {
      message.success('公告已建立');
      void qc.invalidateQueries({ queryKey: ['bulletin-admin'] });
      setModalOpen(false);
      form.resetFields();
    },
  });

  const update = useMutation({
    mutationFn: (v: unknown) => apiClient.patch(`/api/bulletin/${editing!.id}`, v),
    onSuccess: () => {
      message.success('公告已更新');
      void qc.invalidateQueries({ queryKey: ['bulletin-admin'] });
      setModalOpen(false);
      form.resetFields();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/bulletin/${id}`),
    onSuccess: () => {
      message.success('公告已刪除');
      void qc.invalidateQueries({ queryKey: ['bulletin-admin'] });
    },
  });

  const archive = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/bulletin/${id}/archive`, {}),
    onSuccess: () => {
      message.success('公告已封存');
      void qc.invalidateQueries({ queryKey: ['bulletin-admin'] });
    },
  });

  const pin = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/bulletin/${id}/pin`, {}),
    onSuccess: () => {
      message.success('已置頂');
      void qc.invalidateQueries({ queryKey: ['bulletin-admin'] });
    },
  });

  const unpin = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/bulletin/${id}/unpin`, {}),
    onSuccess: () => {
      message.success('已取消置頂');
      void qc.invalidateQueries({ queryKey: ['bulletin-admin'] });
    },
  });

  /* ──────── Handlers ──────── */

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ audience: 'all', isPinned: false, publish: true });
    setModalOpen(true);
  };

  const handleEdit = (record: Bulletin) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title,
      body: record.body,
      audience: record.audience,
      isPinned: record.isPinned,
    });
    setModalOpen(true);
  };

  const handleFinish = (values: Record<string, unknown>) => {
    if (editing) {
      // 編輯時不傳 publish 欄位（後端 PATCH schema 不接收）
      const patchPayload: Record<string, unknown> = { ...values };
      delete patchPayload.publish;
      update.mutate(patchPayload);
    } else {
      create.mutate(values);
    }
  };

  /* ──────── 表格欄位 ──────── */

  const columns: ColumnsType<Bulletin> = [
    {
      title: '標題',
      dataIndex: 'title',
      render: (v: string, record) => (
        <Space size={6}>
          {record.isPinned && (
            <Tooltip title="置頂">
              <Pin size={13} color={color.state.warning} />
            </Tooltip>
          )}
          <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</span>
        </Space>
      ),
    },
    {
      title: '受眾',
      dataIndex: 'audience',
      width: 110,
      render: (v: string) => {
        const label = AUDIENCE_OPTIONS.find((o) => o.value === v)?.label ?? v;
        return (
          <StatusPill tone="info" size="sm" variant="outline">
            {label}
          </StatusPill>
        );
      },
    },
    {
      title: '狀態',
      key: '_status',
      width: 100,
      render: (_, record) => {
        const s = deriveStatus(record);
        const cfg = STATUS_CONFIG[s];
        return (
          <StatusPill tone={cfg.tone} size="sm" dot>
            {cfg.label}
          </StatusPill>
        );
      },
    },
    {
      title: '發布時間',
      dataIndex: 'publishedAt',
      width: 160,
      render: (v: string | null) =>
        v ? (
          <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
            {formatDateTime(v)}
          </span>
        ) : (
          <span style={{ color: color.text.muted }}>—</span>
        ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        const status = deriveStatus(record);
        return (
          <Space size={4}>
            <Tooltip title="編輯">
              <Button
                type="text"
                size="small"
                icon={<Edit3 size={14} />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>

            {record.isPinned ? (
              <Tooltip title="取消置頂">
                <Button
                  type="text"
                  size="small"
                  icon={<PinOff size={14} />}
                  onClick={() => unpin.mutate(record.id)}
                />
              </Tooltip>
            ) : (
              <Tooltip title="置頂">
                <Button
                  type="text"
                  size="small"
                  icon={<Pin size={14} />}
                  onClick={() => pin.mutate(record.id)}
                />
              </Tooltip>
            )}

            {/* 已封存的不能再封存 */}
            {status !== 'archived' && (
              <Popconfirm
                title="封存此公告？"
                description="封存後將從公開公告列表消失，但可在此頁面看到。"
                onConfirm={() => archive.mutate(record.id)}
                okText="封存"
                cancelText="取消"
              >
                <Tooltip title="封存">
                  <Button type="text" size="small" icon={<Archive size={14} />} />
                </Tooltip>
              </Popconfirm>
            )}

            <Popconfirm
              title="確定刪除此公告？"
              description="軟刪除後可由管理員從回收站還原。"
              onConfirm={() => remove.mutate(record.id)}
              okText="刪除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="刪除">
                <Button type="text" size="small" danger icon={<Trash2 size={14} />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <ListPageShell
        title="公告管理"
        description="發布、編輯、封存與刪除公告。已發布的公告會出現在「公告列表」頁面。"
        meta={
          <>
            <PageMetaItem
              icon={<Megaphone size={13} />}
              label="總公告數"
              value={`${stats.total} 則`}
            />
            <PageMetaItem label="已發布" value={`${stats.published} 則`} />
            <PageMetaItem label="草稿" value={`${stats.draft} 則`} />
            <PageMetaItem label="已封存" value={`${stats.archived} 則`} />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增公告
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋標題"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            }
            filters={
              <Select<BulletinStatus>
                placeholder="狀態"
                value={filterStatus}
                onChange={setFilterStatus}
                allowClear
                style={{ width: 130 }}
                options={(Object.keys(STATUS_CONFIG) as BulletinStatus[]).map((k) => ({
                  value: k,
                  label: STATUS_CONFIG[k].label,
                }))}
              />
            }
            extra={
              (searchText || filterStatus) && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => {
                    setSearchText('');
                    setFilterStatus(undefined);
                  }}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<Bulletin>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 900 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 筆資料`,
            defaultPageSize: 20,
          }}
        />
      </ListPageShell>

      <Modal
        title={editing ? '編輯公告' : '新增公告'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={640}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ marginTop: 16 }}
          initialValues={{ audience: 'all', isPinned: false, publish: true }}
        >
          <Form.Item
            name="title"
            label="公告標題"
            rules={[{ required: true, max: 200, message: '請輸入標題（最多 200 字）' }]}
          >
            <Input maxLength={200} placeholder="一句話說明公告主題" />
          </Form.Item>

          <Form.Item
            name="audience"
            label="受眾"
            tooltip="決定誰能在公告列表頁看到這則公告"
            rules={[{ required: true }]}
          >
            <Select options={AUDIENCE_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="body"
            label="公告內文"
            rules={[{ required: true, message: '請輸入內文' }]}
            tooltip="支援 HTML（後續可升級為富文本編輯器）"
          >
            <Input.TextArea rows={6} placeholder="完整內容..." />
          </Form.Item>

          <Form.Item
            name="isPinned"
            label="是否置頂"
            valuePropName="checked"
            tooltip="置頂的公告會固定在公告列表最上方"
          >
            <Switch checkedChildren="置頂" unCheckedChildren="一般" />
          </Form.Item>

          {/* 立即發布開關僅在「新增」時顯示；編輯時若要改變發布狀態應透過封存或重新發布 */}
          {!editing && (
            <Form.Item
              name="publish"
              label="立即發布"
              valuePropName="checked"
              tooltip="關閉時會以草稿形式儲存，不會出現在公告列表頁；可稍後再來編輯重新發布"
            >
              <Switch checkedChildren="立即" unCheckedChildren="存草稿" />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={create.isPending || update.isPending}
              >
                {editing ? '儲存' : '建立'}
              </Button>
              <Button onClick={() => setModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
